import React, { useMemo, useRef } from "react";
import { Provider } from "react-redux";
import { GraphQLClient, ClientError } from "graphql-request";
import isMobileDetector from "is-mobile";
import { unwrapResult } from "@reduxjs/toolkit";
import { backOff } from "exponential-backoff";
import type { BaseplateSize, SceneImage } from "@brickme/project-core/src";
import { scenes } from "@brickme/project-core/src/scenes/index.ts";
import type { OpenCv } from "@brickme/opencv";
import type { MeasurementUnits } from "@brickme/project-core/src/model/formatted-project-size.ts";
import {
	isSupported as isOpenCvSupported,
	notSupportedStub as openCvNotSupportedStub,
} from "@brickme/opencv/browser/support";
import { createStubGtm, initialiseGtm } from "~/frontend-common/gtm.ts";
import LoadingAnimation from "~/components/ui/loading-animation.tsx";
import { createStoreWithLocalStoragePersistence } from "~/store/create-store.ts";
import { WebsiteUrlProvider } from "~/context/website-url.tsx";
import { HeavyProcessRunnerProvider } from "~/context/heavy-process-runner.tsx";
import { BeforeUnloadProvider } from "~/context/before-unload.tsx";
import createFileStorage from "~/file-storage.ts";
import createProcessRunner from "~/utils/create-process-runner.ts";
import isFacebookSourceBrowser from "~/utils/is-facebook-source-browser.ts";
import { initialState as referenceInitialState } from "~/features/reference/state.ts";
import createBuildListener from "~/features/workspace/create-build-listener.ts";
import createAutoSaveListener from "~/features/save-project/auto-save-listener.ts";
import { initialOrderState } from "~/features/order/store-slice.ts";
import {
	refreshAuthenticatedUser,
	initialState as initialUserState,
} from "~/features/user/store-slice.ts";
import { initialState as navInitialState } from "~/features/nav/store-slice.ts";
import type { AppStore } from "~/store/types.d.ts";
import { createPersistCountryService } from "~/store/persist-country.ts";
import { getPossibleInitialActionFromUrl } from "~/features/nav/initial-action.ts";
import { createAuthService } from "@brickme/frontend-common/src/auth/auth-service.ts";
import { createAuthCookieStorage } from "@brickme/frontend-common/src/auth/auth-cookie-storage.ts";
import { createIpInfoGeolocationService } from "@brickme/frontend-common/src/ip-info/ip-info-service.ts";
import GorgiasProvider from "~/context/gorgias.tsx";
import type { GtmJavascriptOptions } from "~/frontend-common/snippets.ts";
import App from "./app.tsx";
import { OpenCvProvider } from "~/context/open-cv.tsx";

type AppContainerProps = {
	readonly siteCode: string;
	readonly websiteUrl: string;
	readonly apiUrl: string;
	readonly cognitoPoolConfig: {
		readonly id: string;
		readonly appClientId: string;
	};
	readonly shopifyConfig: {
		readonly shopDomain: string;
		readonly storefrontAccessToken: string;
	};
	readonly platformCookieDomain: string;
	readonly gorgiasId: string;
	readonly gtmOptions: GtmJavascriptOptions | undefined;
	readonly userAgent: string;
	readonly ipInfoToken: string;
	readonly url: string;
	readonly version: string;
	readonly defaultCountryCode: string;
	readonly mediaBaseUrl: string;
	readonly basePlateSizes: readonly BaseplateSize[];
	readonly maxNumberOfSelectableBasePlates: number;
	readonly defaultMeasurementUnits: MeasurementUnits;
};

function AppContainer({
	websiteUrl,
	apiUrl,
	gtmOptions,
	cognitoPoolConfig,
	shopifyConfig,
	platformCookieDomain,
	gorgiasId,
	url,
	basePlateSizes,
	userAgent,
	ipInfoToken,
	version,
	defaultCountryCode,
	mediaBaseUrl,
	siteCode,
	maxNumberOfSelectableBasePlates,
	defaultMeasurementUnits,
}: AppContainerProps) {
	if (basePlateSizes.length === 0) {
		throw new Error("No baseplate sizes provided");
	}

	// Shopify
	const shopifyClient = useMemo(
		() =>
			new GraphQLClient(
				`https://${shopifyConfig.shopDomain}/api/2022-10/graphql.json`,
				{
					headers: {
						"X-Shopify-Storefront-Access-Token":
							shopifyConfig.storefrontAccessToken,
					},
				},
			),
		[shopifyConfig],
	);

	// OpenCV
	const openCvLoader = useMemo<Promise<OpenCv>>(async () => {
		if (isOpenCvSupported) {
			const module = await import("@brickme/opencv/browser");
			return module.default;
		}
		return openCvNotSupportedStub;
	}, []);

	// Auth service
	const authService = useMemo(
		() =>
			createAuthService({
				userPoolId: cognitoPoolConfig.id,
				appClientId: cognitoPoolConfig.appClientId,
				originSite: siteCode,
			}),
		[cognitoPoolConfig, siteCode],
	);

	// Api
	const storeRef = useRef<AppStore>();
	const apiClient = useMemo(() => {
		return new GraphQLClient(apiUrl, {
			async fetch(url, options) {
				return backOff(() => fetch(url, options), {
					startingDelay: 500,
					numOfAttempts: 10,
					timeMultiple: 2,
					maxDelay: 4_000,
				});
			},
			async requestMiddleware(request) {
				const store = storeRef.current;
				if (!store) {
					throw new Error("Store not initialised yet");
				}
				const { user } = store.getState().user;
				if (user.type !== "signedIn") {
					return request;
				}

				let { accessToken } = user.user;
				const { idToken, refreshToken } = user.user;

				// Validate tokens using the shared auth service
				const validatedUser = await authService.validateTokens(
					accessToken,
					idToken,
					refreshToken,
				);
				if (!validatedUser) {
					// Tokens are invalid/expired, refresh them
					const result = await store.dispatch(refreshAuthenticatedUser());
					const refreshedUser = unwrapResult(result);
					accessToken = refreshedUser.accessToken;
				}

				const headers = new Headers(request.headers);
				headers.set(`Authorization`, accessToken.accessToken);
				return {
					...request,
					headers,
				};
			},
			// This cuts the error down from a full json response + message
			responseMiddleware(response) {
				if (response instanceof ClientError && response.response.errors) {
					// eslint-disable-next-line no-param-reassign
					response.message = response.response.errors
						.map((e) => e.message)
						.join(", ");
				}
			},
		});
	}, [apiUrl, authService]);

	// Heavy process runner - manage workloads
	const runHeavyProcess = useMemo(() => createProcessRunner(), []);

	const gtm = useMemo(
		() => (gtmOptions ? initialiseGtm(gtmOptions) : createStubGtm()),
		[gtmOptions],
	);

	// Redux store
	// Note: React.StrictMode makes this run twice in dev - only runs once in prod
	const store = useMemo(() => {
		const initialAction = getPossibleInitialActionFromUrl(url);
		const isMobile = isMobileDetector({ tablet: true });
		const isSimpleCreatorOpen = isMobile;

		const initialNumberOfBaseplates = (() => {
			const { searchParams } = new URL(url);
			const widthInt = parseInt(
				searchParams.get("numberOfBaseplatesWidth") ?? "",
				10,
			);
			const heightInt = parseInt(
				searchParams.get("numberOfBaseplatesHeight") ?? "",
				10,
			);
			if (
				!Number.isNaN(widthInt) &&
				widthInt > 0 &&
				!Number.isNaN(heightInt) &&
				heightInt > 0
			) {
				return { width: widthInt, height: heightInt };
			}
			return undefined;
		})();

		const injectFullMediaUrl = (image: SceneImage) => ({
			...image,
			imageSrc: `${mediaBaseUrl}/${image.imageSrc}`,
		});

		const newStore = createStoreWithLocalStoragePersistence(
			{
				openCvLoader,
				gtm,
				authService,
				cookieStorage: createAuthCookieStorage({
					appClientId: cognitoPoolConfig.appClientId,
					cookieDomain: platformCookieDomain,
					cookiesSecure: window.location.protocol === "https",
				}),
				apiClient,
				shopifyClient,
				apiTempMediaStorage: createFileStorage(apiClient),
				ipGeolocationService: createIpInfoGeolocationService(ipInfoToken),
				persistCountryService: createPersistCountryService({
					secure: window.location.protocol === "https:",
					cookieDomain: platformCookieDomain,
				}),
			},
			{
				reference: {
					...referenceInitialState,
					maxNumberOfSelectableBasePlates,
					scenes: scenes.map((s) => ({
						...s,
						fullSize: injectFullMediaUrl(s.fullSize),
						mobileSize: injectFullMediaUrl(s.mobileSize),
					})),
					isMobile,
					version,
					defaultCountryCode,
					initialNumberOfBaseplates,
					siteCode,
					shopifyShopDomain: shopifyConfig.shopDomain,
					basePlateSizes,
					os: userAgent.toLowerCase().includes("android") ? "android" : "other",
					browserSource: isFacebookSourceBrowser(userAgent)
						? "facebook"
						: "other",
				},
				nav: {
					...navInitialState,
					initialAction,
					isSimpleCreatorOpen,
				},
				order: initialOrderState,
				user: {
					...initialUserState,
					measurementsPreference: defaultMeasurementUnits,
				},
			},
		);
		newStore.subscribe(
			createBuildListener(newStore, openCvLoader, runHeavyProcess),
		);
		newStore.subscribe(createAutoSaveListener(newStore));

		storeRef.current = newStore;
		return newStore;
	}, [
		cognitoPoolConfig.appClientId,
		url,
		gtm,
		authService,
		apiClient,
		shopifyClient,
		shopifyConfig,
		runHeavyProcess,
		platformCookieDomain,
		ipInfoToken,
		userAgent,
		version,
		siteCode,
		defaultCountryCode,
		basePlateSizes,
		mediaBaseUrl,
		maxNumberOfSelectableBasePlates,
		defaultMeasurementUnits,
		openCvLoader,
	]);

	if (!store) {
		return <LoadingAnimation>Loading...</LoadingAnimation>;
	}

	return (
		<Provider store={store}>
			<GorgiasProvider id={gorgiasId}>
				<WebsiteUrlProvider url={websiteUrl}>
					<HeavyProcessRunnerProvider runProcess={runHeavyProcess}>
						<OpenCvProvider openCv={openCvLoader}>
							<BeforeUnloadProvider>
								<App />
							</BeforeUnloadProvider>
						</OpenCvProvider>
					</HeavyProcessRunnerProvider>
				</WebsiteUrlProvider>
			</GorgiasProvider>
		</Provider>
	);
}

export default AppContainer;
